import React, { Fragment, useState, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import { Alert } from "../../components/Alert.jsx";
import { ActiveConstraintsBar } from "../../components/ui-components/ActiveConstraintsBar.jsx";
import { PieChartComponent } from "../../components/chart-components/PieChartComponent.jsx";
import { BarChartComponent } from "../../components/BarChartComponent.js";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useQuery } from "@tanstack/react-query";
import {
	retrieveCostPerProject,
	retrieveCostPerOrganisation,
	retrieveCostPerCostComponentGroup,
	retrieveCostPerProjectPerMonth,
	retrieveCostPerOrganisationPerMonth,
	retrieveCostPerCostComponentGroupPerMonth,
	retrieveHiddenCosts,
} from "./api.js";
import { useKeycloak } from "@react-keycloak/web";


const OrganizationL3_Overview = () => {
	const [filters, setFilters] = useState([]);
	const [costTrend, ] = useState([]);
	const [totalCost, setTotalCost] = useState(0);
	const [costPerProject, setCostPerProject] = useState([]);
	const [costPerOrganisation, setCostPerOrganisation] = useState([]);
	const [projectCostPerMonth, setProjectCostPerMonth] = useState(0);
	const [organisationCostPerMonth, setOrganisationCostPerMonth] = useState(0);
	const [costComponentCostPerMonth, setCostComponentCostPerMonth] = useState(0);
	const [costPerCostComponentGroup, setCostPerCostComponentGroup] = useState(0);
	const [hiddenCosts, setHiddenCosts] = useState(0);
	const { keycloak } = useKeycloak();
	const level = "3";

	const { startDate,  endDate } =
		useOutletContext();

	// initialization for the variables for the fuzzy search, the filters and the grouping
	const filtersRef = useRef();
	filtersRef.current = filters;

	useQuery(
		["hiddenCosts", level, filtersRef, startDate, endDate],
		() => retrieveHiddenCosts(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => {
				setHiddenCosts(data.HiddenCosts);
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	useQuery(
		["costPerProject", level, filtersRef, startDate, endDate],
		() => retrieveCostPerProject(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => {
				calcTotalCost(data.CloudCost.details);
				setCostPerProject(data.CloudCost.details);
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	useQuery(
		["costPerOrganisation", level, filtersRef, startDate, endDate],
		() => retrieveCostPerOrganisation(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => setCostPerOrganisation(data.CloudCost.details),
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	useQuery(
		["costPerCostComponentGroup", level, filtersRef, startDate, endDate],
		() =>
			retrieveCostPerCostComponentGroup(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => setCostPerCostComponentGroup(data.CloudCost.details),
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	useQuery(
		["costPerProjectPerMonth", level, filtersRef, startDate, endDate],
		() => retrieveCostPerProjectPerMonth(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => {
				if (data.CloudCost && data.CloudCost.details) {
					data.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
					});

					setProjectCostPerMonth(data.CloudCost);
				} else {
					setProjectCostPerMonth([]);
				}
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	useQuery(
		["costPerOrganisationPerMonth", level, filtersRef, startDate, endDate],
		() =>
			retrieveCostPerOrganisationPerMonth(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => {
				if (data.CloudCost && data.CloudCost.details) {
					data.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
					});

					setOrganisationCostPerMonth(data.CloudCost);
				} else {
					setOrganisationCostPerMonth([]);
				}
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	useQuery(
		["costPerCostComponentGroupPerMonth", level, filtersRef, startDate, endDate],
		() =>
			retrieveCostPerCostComponentGroupPerMonth(level, keycloak.token, filtersRef, startDate, endDate),
		{
			onSuccess: (data) => {
				if (data.CloudCost && data.CloudCost.details) {
					data.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
					});

					setCostComponentCostPerMonth(data.CloudCost);
				} else {
					setCostComponentCostPerMonth([]);
				}
			},
			enabled:
				keycloak.token !== "" &&
				filtersRef.current !== undefined &&
				startDate !== null &&
				endDate !== null,
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	function calcTotalCost(data) {
		let currentPeriod = 0;
		let previousPeriod = 0;

		if (data !== null && data.length > 0) {
			data.forEach((row) => {
				currentPeriod += row.cost;
			});
		}

		setTotalCost({
			currentValue: currentPeriod,
			previousValue: previousPeriod,
		});
	}

	return (
		<Fragment>
			<AdministrationHeader
				title="Organisational Overview"
				className="text-text-3"
				icon={icon({name: "folder-tree", style: "light"})}
			/>
			<ActiveConstraintsBar
				token={keycloak.token}
				filters={filters}
				setFilters={setFilters}
				excludeCategories={[
					"organisation l1",
					"organisation l2",
					"organisation l4",
					"organisation l5",
					"organisation l6",
					"organisation l7",
					"organisation l8",
					"organisation l9",
					"organisation l10",
				]}
			/>

			<div className="text-left flex flex-auto flex-col ml-10 border-solid border-border">
				<div className="flex flex-row justify-between">
					<KPI
						title="Total Costs"
						currentValue={totalCost.currentValue}
						trendData={costTrend}
						format="currency"
						showChange={false}
					/>

					{hiddenCosts > 0 && (
						<Alert
							title="Costs not shown"
							value={hiddenCosts}
							description="is allocated to organisations that are not at the current organizational level or below"
							format="currency"
							backgroundColor={"warning-soft"}
						/>
					)}
				</div>

				<div className=" py-2 my-2 rounded-lg h-screen w-screen flex flex-auto flex-col">
					{/* <div className="text-coolgrey3 text-xl font-semibold mt-4 mb-2">
                        Cost per Project
                    </div> */}
					<div className=" rounded-lg h-[260px] w-full flex flex-row">
						<div className="w-[33%]">
							<PieChartComponent
								data={costPerOrganisation}
								value="cost"
								name="organisation_name"
								title="Organisation"
							/>
						</div>
						<div className="w-[33%]">
							<PieChartComponent
								data={costPerProject}
								value="cost"
								name="project_name"
								title="Account"
							/>
						</div>
						<div className="w-[33%]">
							<PieChartComponent
								data={costPerCostComponentGroup}
								value="cost"
								name="cost_resource_type"
								title="Cost resource cluster"
							/>
						</div>
					</div>

					<div className=" rounded-lg mt-12 h-[260px] w-full flex flex-row">
						<div className="w-[33%]">
							<BarChartComponent
								data={organisationCostPerMonth.details}
								dateFormat="MMM YYYY"
								title="Organisation per month"
								valueName="Total Costs"
								legendPosition="right"
							/>
						</div>
						<div className="w-[33%]">
							<BarChartComponent
								data={projectCostPerMonth.details}
								dateFormat="MMM YYYY"
								title="Account per month"
								valueName="Total Costs"
								legendPosition="right"
							/>
						</div>
						<div className="w-[33%]">
							<BarChartComponent
								data={costComponentCostPerMonth.details}
								dateFormat="MMM YYYY"
								title="Cost resource cluster per month"
								valueName="Total Costs"
								legendPosition="right"
							/>
						</div>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default OrganizationL3_Overview;
